// src/pages/ImagePrepFCA.tsx
// Image Preparation for Frosted Cake Art (hidden/unlinked)
//
// Fixes:
// - Global WM enable + transparent BG toggles affect previews immediately
// - Per-card "WM Transparent BG" toggle added
// - Editor overlay buttons pinned in footer (left); Save/Cancel/Revert on right
// - "Undo to Bulk" renamed to "Revert"
// - Multiple image overlays supported
// - Card controls (WM on/off, position, transparent BG) re-render that card immediately
// - Subtle transparent border on cards
//
// Deps: react, jszip, file-saver, lucide-react, Tailwind

import React, { useCallback, useEffect, useRef, useState } from "react";
import JSZip from "jszip";
import { useNoIndex } from "@/hooks/useNoIndex";
import { saveAs } from "file-saver";
import {
  X, Upload, Image as ImageIcon, Sparkles, Settings2, Download,
  Eye, EyeOff, Undo2, Type, Layers, Trash2
} from "lucide-react";

/* ================= Types ================= */

type Section = "Hero" | "About" | "Gallery" | "Recognition" | "Pricing" | "Custom";
type WatermarkPosition = "top-left" | "top-right" | "bottom-left" | "bottom-right" | "center";

interface SeoContext {
  brand?: string;
  location?: string;
  audience?: string;
  keywords?: string;
  styleHints?: string;
}

interface BatchOptions {
  section: Section;
  outputMaxWidth: number; // pixels (target width)
  outputQuality: number; // 0..1
  // NEW sizing controls
  outputTargetHeight: number;                 // target height (px)
  aspectMode: "keep" | "cover" | "contain";   // resize strategy
  padColor: "#ffffff" | "#000000";            // for "contain"
  // Watermark controls
  watermarkEnabled: boolean;
  wmPosition: WatermarkPosition;
  wmOpacity: number; // 0..1
  wmScale: number;   // scale relative to shorter side (0.05..0.5)
  wmMargin: number;  // px
  // NEW: bulk transparent BG removal
  wmRemoveBg?: boolean;
}

type OverlayText = {
  id: string;
  type: "text";
  text: string;
  fontFamily: string;
  fontSize: number;
  bold: boolean;
  italic: boolean;
  color: string;
  opacity: number;
  x: number; // 0..1
  y: number; // 0..1
  maxWidthPct?: number; // 0..1
};

type OverlayImage = {
  id: string;
  type: "image";
  src: string;    // dataURL
  opacity: number;
  scale: number;  // vs short side
  x: number;      // 0..1
  y: number;      // 0..1
};

type OverlayLayer = OverlayText | OverlayImage;

interface PerImageOverride {
  watermarkEnabled?: boolean;
  wmPosition?: WatermarkPosition;
  wmOpacity?: number;
  wmScale?: number;
  wmMargin?: number;
  wmRemoveBg?: boolean;   // NEW: per-image transparent watermark background
  overlays?: OverlayLayer[];   // text/image overlays
  alt?: string;
  title?: string;
  caption?: string;
  fileName?: string;
  tags?: string[];
}

interface LoadedImageItem {
  id: string;
  file: File;
  src: string;
  width: number;
  height: number;
  preview?: string;
  overrides: PerImageOverride;
  // NEW: last processed dimensions (for UI display)
  outW?: number;
  outH?: number;
}

/* ============== Presets & SEO defaults ============== */

// Expected (recommended) display sizes per section
const sectionSize: Record<Exclude<Section, "Custom">, { width: number; height: number }> = {
  Hero: { width: 2560, height: 1440 },
  About: { width: 1600, height: 900 },
  Gallery: { width: 1400, height: 1400 },
  Recognition: { width: 1200, height: 800 },
  Pricing: { width: 1200, height: 900 },
};

// per-section enforced rendering behavior (non-Custom only)
const sectionPreset: Record<Exclude<Section, "Custom">, {
  aspectMode: BatchOptions["aspectMode"];
  padColor: "#ffffff" | "#000000";
  qualityMin: number;             // clamp floor
}> = {
  Hero:         { aspectMode: "cover",  padColor: "#000000", qualityMin: 0.82 },
  About:        { aspectMode: "cover",  padColor: "#ffffff", qualityMin: 0.82 },
  Gallery:      { aspectMode: "cover",  padColor: "#ffffff", qualityMin: 0.85 },
  Recognition:  { aspectMode: "contain",padColor: "#ffffff", qualityMin: 0.8  },
  Pricing:      { aspectMode: "contain",padColor: "#ffffff", qualityMin: 0.8  },
};

const DEFAULT_SEO: SeoContext = {
  brand: "Frosted Cake Art",
  location: "Cleveland, OH",
  audience: "engaged couples, event planners, parents, graduates",
  keywords: [
    "wedding cakes cleveland","custom wedding cake","sculpted cakes","birthday cakes cleveland",
    "celebration cakes","graduation cakes","bakery near me","custom bakery cleveland",
    "bakery chagrin falls","cakes chagrin falls","custom cakes pepper pike","solon wedding cakes",
    "buttercream wedding cake","fondant cake","lambeth piping","vintage cake",
    "luxury wedding cake","floral cake","textured buttercream","celebrity baker cleveland"
  ].join(", "),
  styleHints: "buttercream, fondant, floral, textured, modern, sculpted",
};

const FONT_CHOICES = [
  "Inter, system-ui, sans-serif",
  "Helvetica, Arial, sans-serif",
  "Georgia, serif",
  "Times New Roman, Times, serif",
  "Garamond, serif",
  "Courier New, Courier, monospace",
  "Montserrat, sans-serif",
  "Playfair Display, serif",
];

/* ================= Utils ================= */

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function makeTransparentWatermark(img: HTMLImageElement, threshold = 245): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = img.naturalWidth; c.height = img.naturalHeight;
  const g = c.getContext("2d")!;
  g.drawImage(img, 0, 0);
  const d = g.getImageData(0, 0, c.width, c.height);
  const a = d.data;
  for (let i = 0; i < a.length; i += 4) {
    const r = a[i], gg = a[i+1], b = a[i+2];
    if (r >= threshold && gg >= threshold && b >= threshold) a[i+3] = 0;
  }
  g.putImageData(d, 0, 0);
  return c;
}

function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number | undefined,
  lineHeight: number
) {
  if (!maxWidth || maxWidth <= 0) { ctx.fillText(text, x, y); return; }
  const words = text.split(" ");
  let line = "", yPos = y;
  for (let n = 0; n < words.length; n++) {
    const test = line + (line ? " " : "") + words[n];
    const w = ctx.measureText(test).width;
    if (w > maxWidth && n > 0) { ctx.fillText(line, x, yPos); line = words[n]; yPos += lineHeight; }
    else { line = test; }
  }
  if (line) ctx.fillText(line, x, yPos);
}

/* Base + (optional) WM + overlays → canvas (aspect-aware) */
async function drawAll(
  base: HTMLImageElement,
  wmImg: HTMLImageElement | null,
  opts: Required<BatchOptions>,
  per?: PerImageOverride
): Promise<HTMLCanvasElement> {
  // Target box
  const tw = Math.max(8, Math.round(opts.outputMaxWidth));
  const th = Math.max(8, Math.round(opts.outputTargetHeight));
  const mode = opts.aspectMode; // "keep" | "cover" | "contain"

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;

  if (mode === "keep") {
    // Scale by width only (no crop)
    const scale = Math.min(1, tw / base.width);
    const outW = Math.round(base.width * scale);
    const outH = Math.round(base.height * scale);
    canvas.width = outW;
    canvas.height = outH;
    ctx.drawImage(base, 0, 0, outW, outH);
  } else if (mode === "cover") {
    // Fill tw×th by cropping overflow (CSS background-size: cover)
    const srcAR = base.width / base.height;
    const dstAR = tw / th;
    let sx = 0, sy = 0, sw = base.width, sh = base.height;

    if (srcAR > dstAR) {
      // wider → crop left/right
      const targetW = base.height * dstAR;
      sx = Math.floor((base.width - targetW) / 2);
      sw = Math.floor(targetW);
    } else {
      // taller → crop top/bottom
      const targetH = base.width / dstAR;
      sy = Math.floor((base.height - targetH) / 2);
      sh = Math.floor(targetH);
    }

    canvas.width = tw;
    canvas.height = th;
    ctx.drawImage(base, sx, sy, sw, sh, 0, 0, tw, th);
  } else {
    // "contain" → fit inside tw×th with letterbox padding
    const srcAR = base.width / base.height;
    const dstAR = tw / th;
    let dx = 0, dy = 0, dw = tw, dh = th;

    if (srcAR > dstAR) {
      // fit width
      dw = tw;
      dh = Math.round(tw / srcAR);
      dx = 0;
      dy = Math.floor((th - dh) / 2);
    } else {
      // fit height
      dh = th;
      dw = Math.round(th * srcAR);
      dy = 0;
      dx = Math.floor((tw - dw) / 2);
    }

    canvas.width = tw;
    canvas.height = th;
    ctx.fillStyle = opts.padColor; // "#ffffff" or "#000000"
    ctx.fillRect(0, 0, tw, th);
    ctx.drawImage(base, dx, dy, dw, dh);
  }

  const outW = canvas.width;
  const outH = canvas.height;

  // ----- Watermark -----
  const wmEnabled = (per?.watermarkEnabled ?? opts.watermarkEnabled) && wmImg;
  if (wmEnabled && wmImg) {
    let wmSource: CanvasImageSource = wmImg;

    // Per-image transparent BG (if your overrides include wmRemoveBg)
    const removeBg = (per?.wmRemoveBg ?? opts.wmRemoveBg) === true;
    if (removeBg) {
      const tmp = document.createElement("canvas");
      tmp.width = wmImg.naturalWidth; tmp.height = wmImg.naturalHeight;
      const tg = tmp.getContext("2d")!;
      tg.drawImage(wmImg, 0, 0);
      const d = tg.getImageData(0, 0, tmp.width, tmp.height);
      const a = d.data;
      for (let i = 0; i < a.length; i += 4) {
        const r = a[i], g = a[i + 1], b = a[i + 2];
        if (r >= 245 && g >= 245 && b >= 245) a[i + 3] = 0; // knock out near-white
      }
      tg.putImageData(d, 0, 0);
      wmSource = tmp;
    }

    const shorter = Math.min(outW, outH);
    const t = Math.max(8, shorter * (per?.wmScale ?? opts.wmScale));
    const aspect = (wmImg.naturalWidth || 1) / (wmImg.naturalHeight || 1);
    const wmW = t * aspect;
    const wmH = t;
    const margin = per?.wmMargin ?? opts.wmMargin;
    const pos = per?.wmPosition ?? opts.wmPosition;

    let x = 0, y = 0;
    switch (pos) {
      case "top-left": x = margin; y = margin; break;
      case "top-right": x = outW - wmW - margin; y = margin; break;
      case "bottom-left": x = margin; y = outH - wmH - margin; break;
      case "bottom-right": x = outW - wmW - margin; y = outH - wmH - margin; break;
      case "center": x = (outW - wmW) / 2; y = (outH - wmH) / 2; break;
    }

    const opacity = per?.wmOpacity ?? opts.wmOpacity;
    ctx.globalAlpha = opacity;
    ctx.shadowColor = "rgba(0,0,0,0.25)";
    ctx.shadowBlur = 2; ctx.shadowOffsetX = 1; ctx.shadowOffsetY = 1;
    ctx.drawImage(wmSource, x, y, wmW, wmH);
    ctx.globalAlpha = 1;
    ctx.shadowColor = "transparent"; ctx.shadowBlur = 0;
  }

  // ----- Overlays (unchanged) -----
  const overlays = per?.overlays || [];
  for (const l of overlays) {
    if (l.type === "image") {
      const img = await (async () => { const i = new Image(); await new Promise((r,j)=>{i.onload=r; i.onerror=j; i.src=l.src;}); return i; })();
      const shorter = Math.min(outW, outH);
      const t = Math.max(8, shorter * l.scale);
      const aspect = img.naturalWidth / img.naturalHeight;
      const ow = t * aspect, oh = t;
      const x = Math.round(l.x * (outW - ow));
      const y = Math.round(l.y * (outH - oh));
      ctx.globalAlpha = l.opacity; ctx.drawImage(img, x, y, ow, oh); ctx.globalAlpha = 1;
    } else {
      const weight = l.bold ? "700" : "400";
      const style = l.italic ? "italic " : "";
      ctx.font = `${style}${weight} ${l.fontSize}px ${l.fontFamily || "sans-serif"}`;
      ctx.fillStyle = l.color || "#000";
      ctx.globalAlpha = l.opacity;
      const x = Math.round(l.x * outW);
      const y = Math.round(l.y * outH);
      drawWrappedText(ctx, l.text, x, y, l.maxWidthPct ? l.maxWidthPct * outW : undefined, Math.round(l.fontSize*1.2));
      ctx.globalAlpha = 1;
    }
  }

  return canvas;
}

function canvasToBlob(canvas: HTMLCanvasElement, q: number): Promise<Blob> {
  return new Promise((resolve) => canvas.toBlob((b)=> resolve(b || new Blob()), "image/jpeg", q));
}

// Always force clean .jpg names
function safeZipName(name: string, fallback = "image.jpg"): string {
  const seg = (name || "").split("/").pop()!.split("\\").pop()!;
  const seed = seg || fallback;
  const cleaned = seed.replace(/[^a-z0-9._-]/gi, "-").replace(/-+/g, "-");
  // strip any trailing extension (png/jpeg/jpg/etc) and any trailing dots
  const stem = cleaned.replace(/\.[a-z0-9]+$/i, "").replace(/\.+$/, "");
  const finalStem = stem || "image";
  const withExt = `${finalStem}.jpg`;
  return withExt.slice(0, 120);
}

function generateMetadata(
  baseName: string,
  section: Section,
  seo: SeoContext,
  originalW: number,
  originalH: number
) {
  const clean = (s?: string) => (s || "").trim();
  const brand = clean(seo.brand);
  const location = clean(seo.location);
  const audience = clean(seo.audience);
  const kws = clean(seo.keywords);
  const style = clean(seo.styleHints);

  const baseTokens = [brand, location, section.toLowerCase(), style, kws]
    .filter(Boolean).join(" ").toLowerCase()
    .replace(/[^a-z0-9\s-]/g,"").replace(/\s+/g,"-").replace(/-+/g,"-");

  const fileName = `${baseTokens ? baseTokens+"-" : ""}${baseName}`
    .toLowerCase().replace(/[^a-z0-9.-]/g,"-").replace(/-+/g,"-");

  const alt = [brand || undefined, section, style || undefined, location || undefined].filter(Boolean).join(" – ");
  const title = `${brand ? brand+" | " : ""}${section} image`;
  const caption = `${brand || "Brand"} ${section.toLowerCase()} visual. Shot styled: ${style || "classic"}. Location: ${location || "N/A"}. Audience: ${audience || "general"}.`;
  const tags = [section.toLowerCase(), ...(kws ? kws.split(",").map(k=>k.trim().toLowerCase()) : [])].filter(Boolean);

  return { fileName, alt, title, caption, tags, width: originalW, height: originalH, section, brand, location };
}

/* ================= Editor Modal (with overlay tools) ================= */

function EditModal({
  open, onClose, item, apply, globalOptions, watermarkSrc
}: {
  open: boolean;
  onClose: () => void;
  item: LoadedImageItem | null;
  apply: (ovr: PerImageOverride) => void;
  globalOptions: BatchOptions;
  watermarkSrc: string | null;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [local, setLocal] = useState<PerImageOverride>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const draggingRef = useRef<{ id: string; dx: number; dy: number } | null>(null);

  useEffect(()=> { setLocal(item?.overrides || {}); setSelectedId(null); }, [item]);

  const overlays = local.overlays || [];

  const redraw = useCallback(async () => {
    if (!item) return;
    const base = new Image(); await new Promise((r,j)=>{ base.onload=r; base.onerror=j; base.src=item.src; });
    let wmImg: HTMLImageElement | null = null;
    if (watermarkSrc) { wmImg = new Image(); await new Promise((r,j)=>{ wmImg!.onload=r; (wmImg as any).onerror=j; wmImg!.src = watermarkSrc; }); }
    const cv = await drawAll(base, wmImg, { ...globalOptions } as Required<BatchOptions>, local);
    const canvas = canvasRef.current; if (!canvas) return;
    canvas.width = cv.width; canvas.height = cv.height;
    canvas.getContext("2d")!.drawImage(cv, 0, 0);
  }, [item, local, globalOptions, watermarkSrc]);

  useEffect(()=> { if (open && item) redraw(); }, [open, item, local, redraw]);

  if (!open || !item) return null;

  function getRel(e: React.MouseEvent<HTMLCanvasElement>) {
    const c = canvasRef.current!; const r = c.getBoundingClientRect();
    return { rx: (e.clientX - r.left) / c.width, ry: (e.clientY - r.top) / c.height };
  }
  const onCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const { rx, ry } = getRel(e);
    if (selectedId) {
      const lay = overlays.find(l => l.id === selectedId);
      if (!lay) return; // guard if selection disappeared
      setLocal(p => ({
        ...p,
        overlays: (p.overlays || []).map(l =>
          l.id === selectedId ? ({ ...l, x: rx, y: ry }) as OverlayLayer : l
        ),
      }));
      return;
    }
    if (overlays.length) {
      let nearest = overlays[0], best = Infinity;
      overlays.forEach(l => {
        const d2 = (l.x - rx)**2 + (l.y - ry)**2;
        if (d2 < best) { best = d2; nearest = l; }
      });
      setSelectedId(nearest.id);
    }
  };
  const onDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!selectedId) return;
    const { rx, ry } = getRel(e);
    const l = overlays.find(o => o.id === selectedId);
    if (!l) return; // guard
    draggingRef.current = { id: selectedId, dx: rx - l.x, dy: ry - l.y };
  };
  const onMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const drag = draggingRef.current;
    if (!drag) return;
    const { rx, ry } = getRel(e);
    const nx = Math.min(Math.max(rx - drag.dx, 0), 1);
    const ny = Math.min(Math.max(ry - drag.dy, 0), 1);
    setLocal(p => {
      const list = p.overlays || [];
      if (!list.find(l => l.id === drag.id)) return p; // guard if layer vanished
      return {
        ...p,
        overlays: list.map(l =>
          l.id === drag.id ? ({ ...l, x: nx, y: ny }) as OverlayLayer : l
        ),
      };
    });
  };
  const onUp = () => { draggingRef.current = null; };

  const addText = () => {
    const layer: OverlayText = {
      id: crypto.randomUUID(),
      type: "text",
      text: "Your text",
      fontFamily: FONT_CHOICES[0],
      fontSize: 36, bold: false, italic: false,
      color: "#000000", opacity: 1, x: 0.1, y: 0.1, maxWidthPct: 0.9
    };
    setLocal(p => ({ ...p, overlays: [ ...(p.overlays || []), layer ] }));
    setSelectedId(layer.id);
  };
  const addImage = async (file: File | null) => {
    if (!file) return;
    const src = await fileToDataUrl(file);
    const layer: OverlayImage = { id: crypto.randomUUID(), type: "image", src, opacity: 1, scale: 0.2, x: 0.1, y: 0.8 };
    setLocal(p => ({ ...p, overlays: [ ...(p.overlays || []), layer ] }));
    setSelectedId(layer.id);
  };
  const updateOverlay = (id: string, patch: Partial<OverlayLayer>) => {
    setLocal(p => ({ ...p, overlays: (p.overlays || []).map(l => l.id===id ? ({ ...l, ...patch }) as OverlayLayer : l) }));
  };
  const removeOverlay = (id: string) => {
    setLocal(p => ({ ...p, overlays: (p.overlays || []).filter(l => l.id !== id) }));
    if (selectedId === id) setSelectedId(null);
  };

  const RevertToBulk = () => {
    setLocal({
      watermarkEnabled: globalOptions.watermarkEnabled,
      wmPosition: globalOptions.wmPosition,
      wmOpacity: globalOptions.wmOpacity,
      wmScale: globalOptions.wmScale,
      wmMargin: globalOptions.wmMargin,
      wmRemoveBg: globalOptions.wmRemoveBg,
      overlays: [],
      alt: undefined, title: undefined, caption: undefined, fileName: undefined, tags: undefined,
    });
    setSelectedId(null);
  };

  const selected = (local.overlays || []).find(l => l.id === selectedId) || null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-7xl h-[95vh] overflow-hidden rounded-2xl bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b">
          <h3 className="text-xl font-semibold">Edit: {item.file.name}</h3>
          <button className="rounded-full p-2 hover:bg-gray-100" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="grid gap-6 md:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] flex-1 p-6 pt-4 overflow-hidden">
          {/* Left: sticky canvas */}
          <div className="h-full md:sticky md:top-[88px]">
            <div className="h-full overflow-hidden rounded-xl border bg-gray-50 p-3">
              <canvas
                ref={canvasRef}
                className="mx-auto h-full w-full object-contain cursor-crosshair"
                onClick={onCanvasClick}
                onMouseDown={onDown}
                onMouseMove={onMove}
                onMouseUp={onUp}
              />
            </div>
          </div>

          {/* Right: controls (scrollable) */}
          <div className="h-full overflow-y-auto pr-1">
            {/* Watermark per-image */}
            <div className="rounded-lg border p-4">
              <h4 className="mb-2 font-medium">Watermark (per image)</h4>
              <div className="mt-1 flex items-center gap-2">
                <input type="checkbox" checked={local.watermarkEnabled ?? true} onChange={(e)=> setLocal(p=>({ ...p, watermarkEnabled: e.target.checked }))} />
                <span className="text-sm text-gray-600">Enable</span>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <input type="checkbox" checked={local.wmRemoveBg ?? globalOptions.wmRemoveBg} onChange={(e)=> setLocal(p=>({ ...p, wmRemoveBg: e.target.checked }))} />
                <span className="text-sm text-gray-600">Make watermark background transparent</span>
              </div>
              <div className="mt-3">
                <label className="text-sm">Position</label>
                <select className="mt-1 w-full rounded-md border p-2" value={local.wmPosition || globalOptions.wmPosition} onChange={(e)=> setLocal(p=>({ ...p, wmPosition: e.target.value as WatermarkPosition }))}>
                  {(["top-left","top-right","bottom-left","bottom-right","center"] as WatermarkPosition[]).map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="mt-3">
                <label className="text-sm">Opacity: {(local.wmOpacity ?? globalOptions.wmOpacity).toFixed(2)}</label>
                <input type="range" min={0.05} max={1} step={0.01} className="w-full" value={local.wmOpacity ?? globalOptions.wmOpacity} onChange={(e)=> setLocal(p=>({ ...p, wmOpacity: Number(e.target.value) }))} />
              </div>
              <div className="mt-3">
                <label className="text-sm">Scale: {(local.wmScale ?? globalOptions.wmScale).toFixed(2)}</label>
                <input type="range" min={0.05} max={0.5} step={0.01} className="w-full" value={local.wmScale ?? globalOptions.wmScale} onChange={(e)=> setLocal(p=>({ ...p, wmScale: Number(e.target.value) }))} />
              </div>
              <div className="mt-3">
                <label className="text-sm">Margin (px): {Math.round(local.wmMargin ?? globalOptions.wmMargin)}</label>
                <input type="range" min={0} max={200} step={1} className="w-full" value={local.wmMargin ?? globalOptions.wmMargin} onChange={(e)=> setLocal(p=>({ ...p, wmMargin: Number(e.target.value) }))} />
              </div>
            </div>

            {/* Overlay list & controls */}
            <div className="mt-4 rounded-lg border p-4">
              <h4 className="mb-2 font-medium inline-flex items-center gap-2"><Layers className="h-4 w-4" /> Overlays</h4>
              {(overlays.length === 0) ? (
                <p className="text-sm text-gray-600">Use the buttons in the footer to add text or image overlays. Click on the canvas to place; drag to move.</p>
              ) : (
                <div className="space-y-3">
                  {overlays.map(l => (
                    <div key={l.id} className={`rounded-md border p-3 ${selectedId===l.id ? "ring-2 ring-black/10" : ""}`}>
                      <div className="mb-2 flex items-center justify-between">
                        <div className="text-sm font-semibold">{l.type === "text" ? "Text Overlay" : "Image Overlay"}</div>
                        <div className="flex items-center gap-2">
                          <button className={`text-xs ${selectedId===l.id ? "text-black" : "text-gray-600"} underline`} onClick={()=> setSelectedId(l.id)}>
                            {selectedId===l.id ? "Selected" : "Select"}
                          </button>
                          <button className="text-xs text-red-600 underline inline-flex items-center gap-1" onClick={()=> removeOverlay(l.id)}>
                            <Trash2 className="h-3 w-3" /> Remove
                          </button>
                        </div>
                      </div>

                      {l.type === "text" ? (
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="md:col-span-2">
                            <label className="text-sm">Text</label>
                            <textarea className="mt-1 w-full rounded-md border p-2" rows={2} value={l.text} onChange={(e)=> updateOverlay(l.id, { text: e.target.value })} />
                          </div>
                          <div>
                            <label className="text-sm">Font family</label>
                            <select className="mt-1 w-full rounded-md border p-2" value={l.fontFamily} onChange={(e)=> updateOverlay(l.id, { fontFamily: e.target.value })}>
                              {FONT_CHOICES.map(f => <option key={f} value={f}>{f.split(",")[0]}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-sm">Font size (px)</label>
                            <input type="number" min={8} max={256} className="mt-1 w-full rounded-md border p-2" value={l.fontSize} onChange={(e)=> updateOverlay(l.id, { fontSize: Number(e.target.value) })} />
                          </div>
                          <div className="flex items-center gap-3">
                            <label className="text-sm">Bold</label>
                            <input type="checkbox" checked={l.bold} onChange={(e)=> updateOverlay(l.id, { bold: e.target.checked })} />
                            <label className="ml-4 text-sm">Italic</label>
                            <input type="checkbox" checked={l.italic} onChange={(e)=> updateOverlay(l.id, { italic: e.target.checked })} />
                          </div>
                          <div>
                            <label className="text-sm">Color</label>
                            <input type="color" className="mt-1 w-12 rounded-md border p-1" value={l.color} onChange={(e)=> updateOverlay(l.id, { color: e.target.value })} />
                          </div>
                          <div>
                            <label className="text-sm">Opacity: {l.opacity.toFixed(2)}</label>
                            <input type="range" min={0} max={1} step={0.01} className="w-full" value={l.opacity} onChange={(e)=> updateOverlay(l.id, { opacity: Number(e.target.value) })} />
                          </div>
                          <div>
                            <label className="text-sm">X (% width)</label>
                            <input type="range" min={0} max={1} step={0.01} className="w-full" value={l.x} onChange={(e)=> updateOverlay(l.id, { x: Number(e.target.value) })} />
                          </div>
                          <div>
                            <label className="text-sm">Y (% height)</label>
                            <input type="range" min={0} max={1} step={0.01} className="w-full" value={l.y} onChange={(e)=> updateOverlay(l.id, { y: Number(e.target.value) })} />
                          </div>
                          <div>
                            <label className="text-sm">Wrap width (% width)</label>
                            <input type="range" min={0} max={1} step={0.01} className="w-full" value={l.maxWidthPct ?? 0.9} onChange={(e)=> updateOverlay(l.id, { maxWidthPct: Number(e.target.value) })} />
                          </div>
                        </div>
                      ) : (
                        <div className="grid gap-3 md:grid-cols-2">
                          <div>
                            <label className="text-sm">Opacity: {l.opacity.toFixed(2)}</label>
                            <input type="range" min={0} max={1} step={0.01} className="w-full" value={l.opacity} onChange={(e)=> updateOverlay(l.id, { opacity: Number(e.target.value) })} />
                          </div>
                          <div>
                            <label className="text-sm">Scale (vs short side): {l.scale.toFixed(2)}</label>
                            <input type="range" min={0.05} max={1} step={0.01} className="w-full" value={l.scale} onChange={(e)=> updateOverlay(l.id, { scale: Number(e.target.value) })} />
                          </div>
                          <div>
                            <label className="text-sm">X (% width)</label>
                            <input type="range" min={0} max={1} step={0.01} className="w-full" value={l.x} onChange={(e)=> updateOverlay(l.id, { x: Number(e.target.value) })} />
                          </div>
                          <div>
                            <label className="text-sm">Y (% height)</label>
                            <input type="range" min={0} max={1} step={0.01} className="w-full" value={l.y} onChange={(e)=> updateOverlay(l.id, { y: Number(e.target.value) })} />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Metadata */}
            <div className="mt-4 rounded-lg border p-4">
              <h4 className="mb-2 font-medium">Metadata</h4>
              <label className="text-sm">File name</label>
              <input className="mt-1 w-full rounded-md border p-2" placeholder="override-filename.jpg" value={local.fileName || ""} onChange={(e)=> setLocal(p=>({ ...p, fileName: e.target.value }))} />
              <div className="mt-3">
                <label className="text-sm">Alt</label>
                <input className="mt-1 w-full rounded-md border p-2" value={local.alt || ""} onChange={(e)=> setLocal(p=>({ ...p, alt: e.target.value }))} />
              </div>
              <div className="mt-3">
                <label className="text-sm">Title</label>
                <input className="mt-1 w-full rounded-md border p-2" value={local.title || ""} onChange={(e)=> setLocal(p=>({ ...p, title: e.target.value }))} />
              </div>
              <div className="mt-3">
                <label className="text-sm">Caption</label>
                <textarea className="mt-1 w-full rounded-md border p-2" rows={2} value={local.caption || ""} onChange={(e)=> setLocal(p=>({ ...p, caption: e.target.value }))} />
              </div>
              <div className="mt-3">
                <label className="text-sm">Tags (comma separated)</label>
                <input className="mt-1 w-full rounded-md border p-2" value={(local.tags || []).join(", ")} onChange={(e)=> setLocal(p=>({ ...p, tags: e.target.value.split(",").map(s=>s.trim()).filter(Boolean) }))} />
              </div>
            </div>
          </div>
        </div>

        {/* Sticky footer: overlay buttons (left) + actions (right) */}
        <div className="sticky bottom-0 w-full border-t bg-white p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button className="inline-flex items-center gap-2 rounded-md border px-3 py-1 text-sm" onClick={addText}><Type className="h-4 w-4" /> Add Text</button>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1 text-sm">
              <Upload className="h-4 w-4" /> Add Image
              <input type="file" accept="image/*" className="hidden" onChange={(e)=> addImage(e.target.files?.[0] || null)} />
            </label>
          </div>
          <div className="flex items-center gap-3">
            <button className="rounded-md border px-4 py-2" onClick={onClose}>Cancel</button>
            <button className="inline-flex items-center gap-2 rounded-md border px-4 py-2" onClick={RevertToBulk}><Undo2 className="h-4 w-4" /> Revert</button>
            <button className="rounded-md bg-black px-4 py-2 text-white" onClick={() => { apply(local); onClose(); }}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= Page ================= */

async function selfCheckZip(blob: Blob): Promise<{ ok: boolean; files: string[]; err?: unknown }> {
  try {
    const z = await JSZip.loadAsync(blob);
    return { ok: true, files: Object.keys(z.files) };
  } catch (err) {
    return { ok: false, files: [], err };
  }
}



export default function ImagePrepFCA() {
  useNoIndex();
  const [images, setImages] = useState<LoadedImageItem[]>([]);
  const [watermarkFile, setWatermarkFile] = useState<File | null>(null);
  const [watermarkSrc, setWatermarkSrc] = useState<string | null>(null);

  const [seo, setSeo] = useState<SeoContext>({ ...DEFAULT_SEO });
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState<LoadedImageItem | null>(null);

  const [options, setOptions] = useState<BatchOptions>({
    section: "Gallery",
    outputMaxWidth: sectionSize["Gallery"].width,
    outputTargetHeight: sectionSize["Gallery"].height,
    outputQuality: Math.max(0.82, sectionPreset["Gallery"].qualityMin),
    aspectMode: sectionPreset["Gallery"].aspectMode,
    padColor: sectionPreset["Gallery"].padColor,
    watermarkEnabled: true,
    wmPosition: "bottom-right",
    wmOpacity: 0.25,
    wmScale: 0.2,
    wmMargin: 24,
    wmRemoveBg: true,
  });

  /* ---------- Loaders ---------- */
  const onSelectImages = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const arr = await Promise.all(
      Array.from(files).map(async (file) => {
        const src = await fileToDataUrl(file);
        const img = new Image(); await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = src; });
        return {
          id: `${file.name}-${crypto.randomUUID()}`,
          file, src,
          width: img.naturalWidth, height: img.naturalHeight,
          overrides: { overlays: [] }, // IMPORTANT: do NOT force watermarkEnabled here; respect global by default
        } as LoadedImageItem;
      })
    );
    setImages((p) => {
      const next = [...p, ...arr];
      // Immediately regenerate previews for the combined list so defaults apply to newly added images
      // (regeneratePreviews uses regenerateOne, so overlays / WM defaults are respected)
      Promise.resolve().then(() => regeneratePreviews()); // defer to next tick so setState lands
      return next;
    });

  };

  const onSelectWatermark = async (file: File | null) => {
    if (!file) { setWatermarkFile(null); setWatermarkSrc(null); return; }
    const src = await fileToDataUrl(file);
    setWatermarkFile(file);
    setWatermarkSrc(src);
  };

  /* ---------- Single preview regenerate ---------- */
  const regenerateOne = useCallback(async (it: LoadedImageItem): Promise<{ url: string; outW: number; outH: number }> => {
    const base = new Image(); await new Promise((r,j)=>{ base.onload=r; base.onerror=j; base.src=it.src; });
    let wmImg: HTMLImageElement | null = null;
    if (watermarkSrc) { wmImg = new Image(); await new Promise((r,j)=>{ wmImg!.onload=r; (wmImg as any).onerror=j; wmImg!.src = watermarkSrc; }); }
  
    // IMPORTANT: pass the full options explicitly (incl. aspect & padColor)
    const fullOpts: Required<BatchOptions> = {
      section: options.section,
      outputMaxWidth: options.outputMaxWidth,
      outputTargetHeight: options.outputTargetHeight,
      outputQuality: options.outputQuality,
      aspectMode: options.aspectMode,
      padColor: options.padColor,
      watermarkEnabled: options.watermarkEnabled,
      wmPosition: options.wmPosition,
      wmOpacity: options.wmOpacity,
      wmScale: options.wmScale,
      wmMargin: options.wmMargin,
      wmRemoveBg: options.wmRemoveBg ?? false,
    };
  
    const cv = await drawAll(base, wmImg, fullOpts, it.overrides);
    const fileName = safeZipName(it.overrides.fileName ?? it.file.name);
    const url = cv.toDataURL("image/jpeg", options.outputQuality);
    return { url, outW: cv.width, outH: cv.height };
  }, [options, watermarkSrc]);

  /* ---------- Batch regenerate ---------- */
  const regeneratePreviews = useCallback(async () => {
    if (!images.length) return;
    setGenerating(true);
    const next = await Promise.all(images.map(async (it) => {
      const { url, outW, outH } = await regenerateOne(it);
      return { ...it, preview: url, outW, outH };
    }));
    setImages(next);
    setGenerating(false);
  }, [images, regenerateOne]);

  // Auto regenerate when global options or watermark src changes
  useEffect(() => { if (images.length) regeneratePreviews(); }, [options, watermarkSrc]);

  /* ---------- Metadata ---------- */
  const ensureMetadata = useCallback(() => {
    setImages(prev => prev.map(it => {
      const baseName = it.file.name.replace(/\.[^.]+$/, "").toLowerCase();
      const meta = generateMetadata(baseName, options.section, seo, it.width, it.height);
      const merged: PerImageOverride = {
        fileName: it.overrides.fileName || meta.fileName,
        alt: it.overrides.alt || meta.alt,
        title: it.overrides.title || meta.title,
        caption: it.overrides.caption || meta.caption,
        tags: it.overrides.tags?.length ? it.overrides.tags : meta.tags,
        watermarkEnabled: it.overrides.watermarkEnabled, // keep as-is (undefined respects global)
        wmPosition: it.overrides.wmPosition,
        wmOpacity: it.overrides.wmOpacity,
        wmScale: it.overrides.wmScale,
        wmMargin: it.overrides.wmMargin,
        wmRemoveBg: it.overrides.wmRemoveBg ?? options.wmRemoveBg,
        overlays: it.overrides.overlays || [],
      };
      return { ...it, overrides: merged };
    }));
  }, [seo, options.section, options.wmRemoveBg]);


  // Guaranteed JPEG payload from a canvas: Blob → Base64 → 1×1 fallback
  async function getJpegPayload(
    cv: HTMLCanvasElement,
    q: number
  ): Promise<{ kind: "blob" | "base64"; data: Blob | string }> {
    const blob = await canvasToBlob(cv, q);
    if (blob && blob.size > 0) return { kind: "blob", data: blob };
  
    const dataUrl = cv.toDataURL("image/jpeg", q);
    const b64 = dataUrl.split(",")[1] || "";
    if (b64.length >= 32) return { kind: "base64", data: b64 };
  
    // last resort 1x1 white pixel
    const tiny = document.createElement("canvas");
    tiny.width = 1; tiny.height = 1;
    const tctx = tiny.getContext("2d");
    if (tctx) { tctx.fillStyle = "#ffffff"; tctx.fillRect(0, 0, 1, 1); }
    const tinyUrl = tiny.toDataURL("image/jpeg", 0.8);
    const tinyB64 = tinyUrl.split(",")[1] || "////";
    return { kind: "base64", data: tinyB64 };
  }

// Make names unique inside the ZIP: foo.jpg, foo-2.jpg, foo-3.jpg, ...
function nextAvailableName(proposed: string, used: Set<string>): string {
  const m = proposed.match(/^(.*?)(\.[^.]+)?$/);
  const stem = (m?.[1] || "image").replace(/\.+$/, "");
  const ext = (m?.[2] || ".jpg");
  let n = `${stem}${ext}`;
  let i = 2;
  while (used.has(n.toLowerCase())) {
    n = `${stem}-${i}${ext}`;
    i++;
  }
  used.add(n.toLowerCase());
  return n;
}


  /* ---------- Export ZIP ---------- */
const downloadZip = useCallback(async () => {
  if (!images.length) return;
  setGenerating(true);

  try {
    const zip = new JSZip();
    const imgFolder = zip.folder("images")!;
    const metaArr: any[] = [];
    let added = 0;
    const usedNames = new Set<string>(); // ensure unique names per export


    // Process each image using the same pipeline as previews (includes edits/overlays)
    for (const it of images) {
      // Get the rendered preview for this item
      const { url } = await regenerateOne(it);

      // Compute final filename ONCE and reuse it everywhere (always ends with .jpg)
      let fileName = safeZipName(it.overrides.fileName ?? it.file.name);
      if (!/\.jpg$/i.test(fileName)) fileName = fileName.replace(/\.+$/, "") + ".jpg";
      fileName = nextAvailableName(fileName, usedNames);


      // Write image data: data URL -> base64; otherwise fetch blob
      if (url.startsWith("data:image/")) {
        const b64 = url.split(",")[1] || "";
        imgFolder.file(fileName, b64, { base64: true });
      } else {
        const resp = await fetch(url);
        const blob = await resp.blob();
        imgFolder.file(fileName, blob);
      }
      added++;

      // Metadata record (uses the SAME fileName)
      metaArr.push({
        original_file: it.file.name,
        output_file: fileName,
        alt: it.overrides.alt,
        title: it.overrides.title,
        caption: it.overrides.caption,
        tags: (it.overrides.tags || []).join(", "),
        section: options.section,
        brand: seo.brand || "",
        location: seo.location || "",
      });
    } // end for

    if (added === 0) {
      alert("No images could be rendered. Please check your inputs and try again.");
      return;
    }

    // Add metadata once, AFTER all images are written
    zip.file("metadata.json", JSON.stringify({ seo, section: options.section, items: metaArr }, null, 2));
    const header = ["original_file","output_file","alt","title","caption","tags","section","brand","location"];
    const csv = [header.join(",")]
      .concat(metaArr.map(r => header.map(h => `"${String(r[h] ?? "").replace(/"/g,'""')}"`).join(",")))
      .join("\n");
    zip.file("metadata.csv", csv);

    // Generate and save compressed ZIP
    const blob = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: { level: 6 },
    });
    saveAs(blob, `image-prep-fca_${options.section.toLowerCase()}.zip`);
  } catch (err) {
    console.error("[ZIP] Fatal error:", err);
    alert("Error while creating ZIP. See console for details.");
  } finally {
    setGenerating(false);
  }
}, [images, options.section, seo, regenerateOne]);

  /* ---------- Per-card controls (instant preview) ---------- */
  const handlePerItemToggle = (id: string) => {
    setImages(prev => {
      const next = prev.map(it => it.id===id ? { ...it, overrides: { ...it.overrides, watermarkEnabled: !(it.overrides.watermarkEnabled ?? options.watermarkEnabled) } } : it);
      const target = next.find(n=>n.id===id)!;
      regenerateOne(target).then(({ url, outW, outH }) =>
        setImages(prev2 => prev2.map(x => x.id===id ? { ...target, preview: url, outW, outH } : x))
      );
      return next;
    });
  };

  const handlePerItemPosition = (id: string, pos: WatermarkPosition) => {
    setImages(prev => {
      const next = prev.map(it => it.id===id ? { ...it, overrides: { ...it.overrides, wmPosition: pos } } : it);
      const target = next.find(n=>n.id===id)!;
      regenerateOne(target).then(({ url, outW, outH }) =>
        setImages(prev2 => prev2.map(x => x.id===id ? { ...target, preview: url, outW, outH } : x))
      );
      return next;
    });
  };

  const handlePerItemTransparent = (id: string, val: boolean) => {
    setImages(prev => {
      const next = prev.map(it => it.id===id ? { ...it, overrides: { ...it.overrides, wmRemoveBg: val } } : it);
      const target = next.find(n=>n.id===id)!;
      regenerateOne(target).then(({ url, outW, outH }) =>
        setImages(prev2 => prev2.map(x => x.id===id ? { ...target, preview: url, outW, outH } : x))
      );
      return next;
    });
  };

  /* ---------- Open/Apply editor ---------- */
  const openEditor = (id: string) => setEditing(images.find(i => i.id===id) || null);
  const applyEditor = (ovr: PerImageOverride) => {
    if (!editing) return;
    const id = editing.id;
    setImages(prev => {
      const next = prev.map(it => it.id===id ? { ...it, overrides: { ...it.overrides, ...ovr } } : it);
      const target = next.find(n=>n.id===id)!;
      regenerateOne(target).then(({ url, outW, outH }) =>
        setImages(prev2 => prev2.map(x => x.id===id ? { ...target, preview: url, outW, outH } : x))
      );
      return next;
    });
  };

  /* ---------- Auto width on section ---------- */
  useEffect(() => {
    setOptions((o) => {
      if (o.section === "Custom") return o; // leave user controls intact
  
      const s = o.section as Exclude<Section, "Custom">;
      const size = sectionSize[s];
      const preset = sectionPreset[s];
      const clampedQ = Math.max(o.outputQuality, preset.qualityMin);
  
      return {
        ...o,
        outputMaxWidth: size.width,
        outputTargetHeight: size.height,
        aspectMode: preset.aspectMode,
        padColor: preset.padColor,
        outputQuality: clampedQ,
      };
    });
  }, [options.section]);

  const sectionOptions: Section[] = ["Hero","About","Gallery","Recognition","Pricing","Custom"];
  const isFixed = options.section !== "Custom";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-col items-center gap-4">
          <div className="w-full">
            <h1 className="text-5xl md:text-6xl font-bold text-center">Image Preparation for Frosted Cake Art</h1>
            <p className="mt-2 text-sm text-gray-600 text-center">Batch optimize images, apply watermark (with transparent BG), add overlays, generate SEO metadata, and download as ZIP.</p>
          </div>
          <div className="flex items-center gap-3">
            <button className="inline-flex items-center gap-2 rounded-xl bg-black px-4 py-2 text-white shadow hover:bg-gray-900" onClick={downloadZip} disabled={!images.length || generating}>
              <Download className="h-4 w-4" /> Download ZIP
            </button>

          </div>
        </div>

        {/* Controls */}
        <div className="grid gap-6 md:grid-cols-3">
          {/* Uploads */}
          <div className="rounded-2xl border bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-lg font-semibold">1) Load Files</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Select images</label>
                <div className="mt-2 flex items-center gap-3">
                  <input type="file" accept="image/*" multiple onChange={(e) => onSelectImages(e.target.files)} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Watermark (single file for batch)</label>
                <div className="mt-2 flex items-center gap-3">
                  <input type="file" accept="image/*" onChange={(e) => onSelectWatermark(e.target.files?.[0] || null)} />
                </div>
                {watermarkSrc && (
                  <div className="mt-3 inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-gray-600">
                    <ImageIcon className="h-4 w-4" /> {watermarkFile?.name}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Batch Options */}
          <div className="rounded-2xl border bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-lg font-semibold">2) Batch Options</h2>
            <div className="grid gap-3">
              <div>
                <label className="text-sm font-medium">Section</label>
                <select className="mt-1 w-full rounded-md border p-2" value={options.section} onChange={(e) => setOptions((o) => ({ ...o, section: e.target.value as Section }))}>
                  {sectionOptions.map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Target width (px)</label>
                  <input
                    type="number" min={64} max={8192} step={1}
                    className="mt-1 w-full rounded-md border p-2"
                    value={options.outputMaxWidth}
                    onChange={(e) => setOptions((o) => ({ ...o, outputMaxWidth: Number(e.target.value) }))}
                    disabled={isFixed}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Target height (px)</label>
                  <input
                    type="number" min={64} max={8192} step={1}
                    className="mt-1 w-full rounded-md border p-2"
                    value={options.outputTargetHeight}
                    onChange={(e) => setOptions((o) => ({ ...o, outputTargetHeight: Number(e.target.value) }))}
                    disabled={isFixed}
                  />
                </div>
              
                <div>
                  <label className="text-sm font-medium">Aspect mode</label>
                  <select
                    className="mt-1 w-full rounded-md border p-2"
                    value={options.aspectMode}
                    onChange={(e) => setOptions((o) => ({ ...o, aspectMode: e.target.value as BatchOptions["aspectMode"] }))}
                    disabled={isFixed}
                  >
                    <option value="keep">Keep (scale by width; no crop)</option>
                    <option value="cover">Cover (crop to fill)</option>
                    <option value="contain">Contain (letterbox)</option>
                  </select>
                </div>
              
                <div>
                  <label className="text-sm font-medium">Letterbox color</label>
                  <div className="mt-2 flex items-center gap-4">
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="padColor"
                        checked={options.padColor === "#ffffff"}
                        onChange={() => setOptions((o) => ({ ...o, padColor: "#ffffff" }))}
                        disabled={isFixed}
                      />
                      White
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="padColor"
                        checked={options.padColor === "#000000"}
                        onChange={() => setOptions((o) => ({ ...o, padColor: "#000000" }))}
                        disabled={isFixed}
                      />
                      Black
                    </label>
                  </div>
                </div>
              
                <div>
                  <label className="text-sm font-medium">JPEG quality</label>
                  <input
                    type="number"
                    min={isFixed ? sectionPreset[options.section as Exclude<Section,"Custom">].qualityMin : 0.5}
                    max={1}
                    step={0.01}
                    className="mt-1 w-full rounded-md border p-2"
                    value={options.outputQuality}
                    onChange={(e) =>
                      setOptions((o) => {
                        const v = Number(e.target.value);
                        if (o.section === "Custom") return { ...o, outputQuality: v };
                        const minQ = sectionPreset[o.section as Exclude<Section,"Custom">].qualityMin;
                        return { ...o, outputQuality: Math.max(v, minQ) };
                      })
                    }
                  />
                  {isFixed && (
                    <div className="mt-1 text-xs text-gray-500">
                      Minimum quality for this section: {
                        sectionPreset[options.section as Exclude<Section,"Custom">].qualityMin.toFixed(2)
                      } (you can only go higher)
                    </div>
                  )}
                </div>
              
                <div className="flex items-end">
                  <div className="text-xs text-gray-600">
                    {options.section !== "Custom" ? (
                      <>
                        <div>
                          <span className="font-semibold">Expected size (section):</span>{" "}
                          {sectionSize[options.section as Exclude<Section, "Custom">].width}×
                          {sectionSize[options.section as Exclude<Section, "Custom">].height}px
                        </div>
                        <div><span className="font-semibold">Your target:</span> {options.outputMaxWidth}×{options.outputTargetHeight}px</div>
                      </>
                    ) : (
                      <div>
                        <span className="font-semibold">Custom target:</span>{" "}
                        {options.outputMaxWidth}×{options.outputTargetHeight}px
                      </div>
                    )}
                    <div><span className="font-semibold">Aspect:</span> {options.aspectMode}</div>
                  </div>
                </div>
              </div>

              <div className="rounded-md border p-3">
                <h3 className="mb-2 text-sm font-semibold">Watermark (Bulk)</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 flex items-center gap-2">
                    <input type="checkbox" checked={options.watermarkEnabled} onChange={(e) => setOptions((o) => ({ ...o, watermarkEnabled: e.target.checked }))} />
                    <span className="text-sm">Enable watermark by default</span>
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!options.wmRemoveBg}
                      onChange={(e) => setOptions((o) => ({ ...o, wmRemoveBg: e.target.checked }))}
                    />
                    <span className="text-sm">Make watermark background transparent</span>
                  </div>
                  <div>
                    <label className="text-sm">Position</label>
                    <select className="mt-1 w-full rounded-md border p-2" value={options.wmPosition} onChange={(e) => setOptions((o) => ({ ...o, wmPosition: e.target.value as WatermarkPosition }))}>
                      {(["top-left","top-right","bottom-left","bottom-right","center"] as WatermarkPosition[]).map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm">Opacity: {options.wmOpacity.toFixed(2)}</label>
                    <input type="range" min={0.05} max={1} step={0.01} className="w-full" value={options.wmOpacity} onChange={(e) => setOptions((o) => ({ ...o, wmOpacity: Number(e.target.value) }))} />
                  </div>
                  <div>
                    <label className="text-sm">Scale: {options.wmScale.toFixed(2)}</label>
                    <input type="range" min={0.05} max={0.5} step={0.01} className="w-full" value={options.wmScale} onChange={(e) => setOptions((o) => ({ ...o, wmScale: Number(e.target.value) }))} />
                  </div>
                  <div>
                    <label className="text-sm">Margin: {options.wmMargin}px</label>
                    <input type="range" min={0} max={200} step={1} className="w-full" value={options.wmMargin} onChange={(e) => setOptions((o) => ({ ...o, wmMargin: Number(e.target.value) }))} />
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <button className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 shadow-sm hover:bg-gray-50" onClick={regeneratePreviews} disabled={!images.length || generating}>
                  <Settings2 className="h-4 w-4" /> Generate Previews
                </button>
              </div>
            </div>
          </div>

          {/* SEO */}
          <div className="rounded-2xl border bg-white p-5 shadow-sm">
            <h2 className="mb-3 text-lg font-semibold">3) SEO Context</h2>
            <div className="grid gap-3">
              <div>
                <label className="text-sm font-medium">Brand</label>
                <input className="mt-1 w-full rounded-md border p-2" value={seo.brand || ""} onChange={(e) => setSeo((s) => ({ ...s, brand: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium">Location</label>
                <input className="mt-1 w-full rounded-md border p-2" value={seo.location || ""} onChange={(e) => setSeo((s) => ({ ...s, location: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium">Audience</label>
                <input className="mt-1 w-full rounded-md border p-2" value={seo.audience || ""} onChange={(e) => setSeo((s) => ({ ...s, audience: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium">Keywords (comma separated)</label>
                <input className="mt-1 w-full rounded-md border p-2" value={seo.keywords || ""} onChange={(e) => setSeo((s) => ({ ...s, keywords: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium">Style hints</label>
                <input className="mt-1 w-full rounded-md border p-2" value={seo.styleHints || ""} onChange={(e) => setSeo((s) => ({ ...s, styleHints: e.target.value }))} />
              </div>
              <div className="pt-2">
                <button className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-white shadow hover:bg-indigo-700" onClick={ensureMetadata} disabled={!images.length}>
                  <Sparkles className="h-4 w-4" /> Generate/Refresh Metadata
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Preview grid */}
        <div className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold">4) Preview & per-image controls</h2>
            <div className="text-sm text-gray-600">{images.length} image(s)</div>
          </div>

          {images.length === 0 ? (
            <div className="rounded-2xl border border-dashed p-10 text-center text-gray-500">
              <p className="mx-auto max-w-xl">Load images to see live previews. Use <b>Edit</b> for overlays and per-image overrides.</p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {images.map((it) => (
                <div key={it.id} className="rounded-2xl border border-gray-200/70 ring-1 ring-black/5 bg-white p-4 shadow-sm">
                  <div className="relative aspect-square w-full overflow-hidden rounded-xl bg-gray-50">
                    <img src={it.preview || it.src} alt={it.file.name} className="h-full w-full object-cover" />
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <ImageIcon className="h-4 w-4" />
                      <span title="Original size">{it.width}×{it.height}</span>
                      {typeof it.outW === "number" && typeof it.outH === "number" && (
                        <span className="text-gray-400">→ {it.outW}×{it.outH}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button className="rounded-lg border px-2 py-1 text-xs" onClick={()=>handlePerItemToggle(it.id)}>
                        {(it.overrides.watermarkEnabled ?? options.watermarkEnabled)
                          ? <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" /> WM on</span>
                          : <span className="inline-flex items-center gap-1"><EyeOff className="h-3 w-3" /> WM off</span>}
                      </button>
                      <select className="rounded-lg border px-2 py-1 text-xs" value={it.overrides.wmPosition || options.wmPosition} onChange={(e)=>handlePerItemPosition(it.id, e.target.value as WatermarkPosition)}>
                        {(["top-left","top-right","bottom-left","bottom-right","center"] as WatermarkPosition[]).map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                      {/* Per-card transparent BG toggle */}
                      <label className="inline-flex items-center gap-1 text-xs">
                        <input type="checkbox" checked={it.overrides.wmRemoveBg ?? options.wmRemoveBg} onChange={(e)=>handlePerItemTransparent(it.id, e.target.checked)} />
                        <span className="rounded border px-2 py-1">WM Transparent BG</span>
                      </label>
                      <button className="rounded-lg bg-gray-900 px-2 py-1 text-xs text-white" onClick={()=>openEditor(it.id)}>Edit</button>
                    </div>
                  </div>

                  <div className="mt-3 rounded-lg bg-gray-50 p-3 text-xs">
                    <div className="truncate font-medium">{safeZipName(it.overrides.fileName || it.file.name)}</div>
                    <div className="mt-1 text-gray-700"><span className="font-semibold">Alt:</span> {it.overrides.alt || "—"}</div>
                    <div className="text-gray-700"><span className="font-semibold">Title:</span> {it.overrides.title || "—"}</div>
                    <div className="text-gray-700"><span className="font-semibold">Tags:</span> {(it.overrides.tags || []).join(", ") || "—"}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Finalize */}
        <div className="mt-8 rounded-2xl border bg-white p-5 shadow-sm">
          <h2 className="mb-3 text-lg font-semibold">5) Finalize</h2>
          <ol className="list-inside list-decimal text-sm text-gray-700">
            <li>Generate/refresh metadata.</li>
            <li>Adjust per image (WM toggles, position, overlays in editor).</li>
            <li>Download ZIP.</li>
          </ol>
        </div>
      </div>

      {/* Editor */}
      {editing && (
        <EditModal
          open={!!editing}
          onClose={() => setEditing(null)}
          item={editing}
          apply={applyEditor}
          globalOptions={options}
          watermarkSrc={watermarkSrc}
        />
      )}
    </div>
  );
}
